import {DateTime} from "luxon";
import {Customer} from "./customer";
import {ACTIVITY_TYPE, AllActivityTypesArray} from "../../types-enums/activity";
import {SortElementProps} from "atmosfair-ui";
import {AirportResponse, FlightCalculationRequestLeg} from "./activity-flight";
import {HotelCalculationRequest, HotelClass, HotelMealPlan, HotelRoomCategory} from "./activity-hotel";
import {
    CarBusCalculatedEmissionsResponse,
    FlightCalculatedEmissionsResponse,
    HotelCalculatedEmissionsResponse
} from "./emissions";
import {DurationProps} from "../../types-enums/activity-common-properties";
import {TravelClass} from "../../screens/activity/activity-form/flight";
import {AgencyProps} from "./agency";
import {CarBusCalculationRequest} from "./activity-car-bus";
import {ScrollDirection} from "./common";
import {WaypointsProps} from "../../types-enums/activity-car-bus";
import {GeoJSON} from "geojson";
import {BundleType} from "../../container/buttons/common";

// This file contains all the types/interfaces that are related to creating, loading, updating or deleting Bundles
// This also means, that there are Activity specific interfaces/types, which are only needed in conjunction with
// CRUD of Bundles

/////////////
// General //
////////////
export enum SortBundle {
    name = "bundlename",
    updated = "updated",
    created = "created",
    userFirstName = "userfirstname",
    agencyName = "agencyname",
    totalCo2KgE = 'totalco2kge'
}

export type CreateOrUpdatedBundledActivities = (CreateOrUpdatedFlightActivity | CreateOrUpdatedHotelActivity | CreateOrUpdateCarBusActivity)

export type LoadedActivity = LoadBundleFlightActivity | LoadBundleHotelActivity | LoadBundleCarBusActivity
export type LoadedActivities = (LoadedActivity)[]

export interface BaseLoadBundleActivity {
    id: number
    updated: string | null,
    day: number | null,
    activityType: ACTIVITY_TYPE,
    previousEmissionsCo2KgE: number | null
}

export interface BaseCreateOrUpdateBundleReturnActivity {
    groupId: string
}

export interface BaseCreateOrUpdateBundleActivity {
    day: number | null,
    co2KgE: number | null
    activityType: ACTIVITY_TYPE,
    id: number | null
}

export interface LoadBundleResponse {
    id: number,
    name: string,
    description: string | null,
    created: string,
    updated: string | null,
    startDate: string | null,
    endDate: string | null,
    durationInDays: number | null,
    customer: Customer,
    userId: string,
    agencyId: number,
    activities: LoadedActivities,
    personCount: number,
    previousTotalCo2KgE: number | null,
    updatedTotalCo2KgE: number | null
}

export interface ReadBundleResponse {
    id: number,
    name: string,
    startDate: string | null, // ISO 8601 compliant
    endDate: string | null, // ISO 8601 compliant
    durationInDays: number | null,
    updated: string | null, // ISO 8601 compliant
    created: string, // ISO 8601 compliant
    agencyId: number,
    agencyName: string | null,
    userId: string,
    userFullName: string,
    customerId: number | null,
    totalCo2KgE: number | null,
}

export interface TransformedBundleResponse extends Omit<ReadBundleResponse, "startDate" | "endDate" | "updated" | "created"> {
    startDate: DateTime | null,
    endDate: DateTime | null,
    updated: DateTime | null,
    created: DateTime,
}

export interface ReadBundleWindowedRequest {
    agencyId: number,
    size: number,
    scrollPosition: String | null,
    sortOrder: SortElementProps["active"],
    direction: ScrollDirection,
    sort: SortBundle,
    whatToGet: BundleType,
    searchTerm: string | null
}

export interface CreateBundleProps {
    name: string,
    description: string | null,
    journeyDuration: DurationProps | null,
    customer: Customer | null,
    agency: AgencyProps,
    personCount: number | null,
    activities: AllActivityTypesArray,
}

export interface ActivityTypeToIdsMap {
    [ACTIVITY_TYPE.hotel]: CreateOrUpdateBundleSingleRowActivityResponse[]
    [ACTIVITY_TYPE.flight]: CreateOrUpdateBundleMultipleRowActivityResponse[]
    [ACTIVITY_TYPE.car_bus]: CreateOrUpdateBundleSingleRowActivityResponse[]
}

export interface CreateBundleSuccessResponse {
    bundleId: number,
    mapOfActivityIds: ActivityTypeToIdsMap,
    created: string, // ISO 8601 compliant
}

export interface UpdateBundle extends CreateBundleProps {
    id: number,
}

export interface WindowedBundleResponse {
    bundles: ReadBundleResponse[];
    nextPosition: string | null;
    bundlesCount: number;
}

export interface TransformedWindowedBundleResponse extends Omit<WindowedBundleResponse, "bundles"> {
    bundles: TransformedBundleResponse[];
}

export interface BundleForStore {
    bundleId: number,
    name: string,
    description: string | null,
    agency: AgencyProps | null,
    customer: Customer,
    journeyDuration: DurationProps | null,
    activities: AllActivityTypesArray,
    created: DateTime,
    updated: DateTime | null,
    userId: string,
    personCount: number,
    previousTotalCo2KgE: number | null
    updatedTotalCo2KgE: number | null
}

////////////
// Flight //
///////////
export interface OriginalAirportProps {
    departureAirport: AirportResponse | null,
    arrivalAirport: AirportResponse | null
}

export interface CreateOrUpdatedFlightActivity extends BaseCreateOrUpdateBundleActivity, FlightCalculationRequestLeg, BaseCreateOrUpdateBundleReturnActivity {
    departureName: string,
    departureCountryAlpha3: string,
    arrivalName: string,
    arrivalCountryAlpha3: string,
    airlineName: string | null,
    aircraftName: string | null,
    returnIds: [string, string] | null,
    frontendIds: [string, string]
}

export interface DiscontinuedFlightNumberProps {
    flightNumber: string,
    flightDate: string,
}

export interface LoadBundleFlightActivity extends BaseLoadBundleActivity {
    index: number,
    departureName: string,
    departureIata: string,
    departureCountryAlpha3: string,
    arrivalName: string,
    arrivalIata: string,
    arrivalCountryAlpha3: string,
    airlineIata: string | null,
    airlineName: string | null,
    aircraftName: string | null,
    aircraftIata: string | null,
    flightNumber: string | null,
    flightDate: string | null,
    flightCount: number,
    passengerCount: number,
    travelClass: TravelClass,
    groupId: string,
    returnFlightId: number | null,
    flightNumberDiscontinued: DiscontinuedFlightNumberProps | null,
    originalAirport: OriginalAirportProps | null,
    emissions: FlightCalculatedEmissionsResponse | null,
}

////////////
// Hotel //
///////////
export interface CreateOrUpdatedHotelActivity extends BaseCreateOrUpdateBundleActivity, HotelCalculationRequest {
    countryAlpha2: string,
    countryName: string,
    startDate: string | null,
    endDate: string | null,
    frontendIds: string[],
}

export interface LoadBundleHotelActivity extends BaseLoadBundleActivity {
    countryAlpha2: string,
    countryAlpha3: string,
    countryName: string,
    giataId: string | null,
    hotelName: string | null,
    city: string,
    region: string | null,
    startDate: string | null,
    endDate: string | null,
    hotelClass: HotelClass,
    roomCategory: HotelRoomCategory,
    numberRooms: number,
    numberNights: number,
    numberPeople: number,
    mealPlan: HotelMealPlan | null,
    hotelDiscontinued: boolean,
    originalHotelName: string | null,
    originalHotelClass: HotelClass | null,
    emissions: HotelCalculatedEmissionsResponse | null
}

export interface CreateOrUpdateBundleSingleRowActivityResponse {
    frontendIds: string[],
    backendId: number
}

export interface CreateOrUpdateBundleMultipleRowActivityResponse {
    frontendIds: string[],
    backendId: number,
    groupId: string,
}

//////////////
// Car/Bus //
/////////////
interface CarBusDistanceProps {
    calculatedDistanceKm: number | null,
    manualEnteredDistanceKm: number | null,
}

export interface CreateOrUpdateCarBusActivity extends BaseCreateOrUpdateBundleActivity, CarBusDistanceProps, Omit<CarBusCalculationRequest, "distance"> {
    waypoints: WaypointsProps,
    route: GeoJSON.LineString | null,
    frontendIds: string[],
}

export interface LoadBundleCarBusActivity extends BaseLoadBundleActivity, CarBusDistanceProps, Omit<CarBusCalculationRequest, "distance"> {
    emissions: CarBusCalculatedEmissionsResponse,
    route: GeoJSON.LineString | null,
    waypoints: WaypointsProps,
}